import cn from 'classnames';
import { Field, useFormikContext } from 'formik';
import { useState, useContext, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import DiscountTag from 'components/common/DiscountTag';
import Text from 'components/common/Text';
import PrivacyPolicy from 'components/shared/PrivacyPolicy';
import TermsOfService from 'components/shared/TermsOfService';
import { UserContext } from 'components/signUpFunnel/Contexts';
import Disclaimer from 'components/signUpFunnel/shared/Disclaimer';
import { TEMPO } from 'context/BrandContext';
import useBrand from 'hooks/useBrand';
import { updateNotificationPreferences } from 'services/RequestService/endpoints/user';

const SmsLabel = ({ checkboxResponseLabel, invalidPhone, smsDisclaimerText }) => (
  <>
    <label htmlFor="smsChecked" className="-mt-6 pl-6">
      <Text
        htmlElement="span"
        className={cn('block pb-2', { 'text-charcoal-70': invalidPhone })}
        brandStylesOverrides="textMd"
      >
        {checkboxResponseLabel}
      </Text>
    </label>
    <Text
      htmlElement="span"
      className={cn('text-sm font-normal', invalidPhone ? 'text-charcoal-70' : 'text-charcoal-90')}
      brandStylesOverrides="textSm"
    >
      By checking the box above, I agree to receive recurring promotional text messages from Home
      Chef at the phone number provided. I also agree to Home Chef's{' '}
      <TermsOfService
        classNames={cn('underline text-sm', invalidPhone ? 'text-charcoal-70' : 'text-charcoal-90')}
        text="Terms and Conditions of Service"
      />{' '}
      and the{' '}
      <PrivacyPolicy
        classNames={cn('underline text-sm', invalidPhone ? 'text-charcoal-70' : 'text-charcoal-90')}
        text="Privacy Policy"
      />
      . Consent is not a condition to purchase. Message & data rates may apply. Reply HELP for help
      & STOP to cancel.
      <Disclaimer
        message={smsDisclaimerText}
        viewPromoDisclaimerCopy
        buttonClassNames={cn(
          'p-0 mt-3 textSm text-sm',
          invalidPhone ? 'text-charcoal-70' : 'text-charcoal-90'
        )}
        textClassNames={cn(
          'mt-2 textSm text-sm',
          invalidPhone ? 'text-charcoal-70' : 'text-charcoal-90'
        )}
      />
    </Text>
  </>
);

SmsLabel.propTypes = {
  invalidPhone: PropTypes.bool.isRequired,
  checkboxResponseLabel: PropTypes.string.isRequired,
  smsDisclaimerText: PropTypes.string,
};

const Sms = ({ setToastMessage }) => {
  const { setUser } = useContext(UserContext);
  const [smsError, setSmsError] = useState('');
  const { handleChange, values, errors, setFieldValue } = useFormikContext();
  const transitionRef = useRef();

  const validPhone = values.phoneNumber && !errors.phoneNumber;

  const isTempo = useBrand(TEMPO);

  const checkboxResponseLabel = isTempo
    ? 'I want to receive texts about offers and menus from Tempo by Home Chef.'
    : 'I want to receive texts about offers, menus, and news from Home Chef.';

  const smsDisclaimerText =
    'Valid only for new SMS customers. Get an additional 5% off your first box. Offer expires 10 weeks after sign up. Limit 1 per household. No cash value. Not valid in all states. Void where prohibited.';

  const handleCheck = async () => {
    const response = await updateNotificationPreferences({
      params: {
        smsPromotional: !values.smsChecked,
        phone: values.phoneNumber,
        smsDiscount: !values.smsChecked,
      },
    });
    if (response.ok) {
      if (response?.user) {
        const message = response.user.receivesSmsPromotional
          ? 'Messaging preferences updated and a 5% discount applied.'
          : 'Your messaging preferences have been updated';
        setUser(prev => ({
          ...prev,
          receivesSmsPromotional: response.user.receivesSmsPromotional,
        }));
        setToastMessage({ message, type: 'success' });
      }
    } else {
      setToastMessage({
        message:
          'Unexpected error updating messaging preferences. Please try again in a few minutes',
        type: 'error',
      });
      setFieldValue('smsChecked', false);
    }
    setTimeout(() => setToastMessage(null), 5000);
  };

  return (
    <section
      className={cn('mx-auto w-full rounded-xl border border-solid p-2', {
        'border-cream-120 bg-cream-110': !isTempo,
        'border-charcoal-60 bg-tempo-gray-100': isTempo,
      })}
    >
      <DiscountTag
        discountText={'Get an extra 5% off'}
        spanClassNames="relative bottom-5 inline-flex"
      />
      <div
        className="pt-2"
        onClick={() => (validPhone ? null : setSmsError('Please enter a phone number to enroll'))}
      >
        <div role="alert">
          <CSSTransition
            in={Boolean(smsError) && !validPhone}
            timeout={200}
            classNames="fadeInOut"
            unmountOnExit
            nodeRef={transitionRef}
          >
            <div ref={transitionRef}>
              <Text className="text-red-100">{smsError}</Text>
            </div>
          </CSSTransition>
        </div>
        <Field
          id="smsChecked"
          aria-label="smsChecked"
          type="checkbox"
          name="smsChecked"
          className=" mt-1"
          onChange={event => {
            handleChange(event);
            handleCheck();
          }}
          disabled={!validPhone}
        />
        <SmsLabel
          invalidPhone={!validPhone}
          checkboxResponseLabel={checkboxResponseLabel}
          smsDisclaimerText={smsDisclaimerText}
        />
      </div>
    </section>
  );
};

Sms.propTypes = {
  setToastMessage: PropTypes.func.isRequired,
};

export default Sms;
